import type { Item } from '@mimir/common/dto/searchResultHit';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { divideFuzzy, fractionToFuzzy } from '@mimir/common/util/fuzzyMath';
import type { BrowserBehavior } from '../util/browserBehavior';
import type { TsStream } from '@mimir/common/dto/liveItemProps';
import { MPEGTS_TIMEBASE } from '@mimir/common/dto/liveDtos';
import { getTimeInSecondsFromTimecodeString } from '@mimir/common/util/timecode';
import type { Fraction } from '@mimir/common/util/fractionType';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { invariant } from '@mimir/common/util/invariant';
import type { FirstFramePtsDelay, FramePtsConversion} from '../time/frameTime';
import type { PlayableArtifact, PlayableItemDto } from '@mimir/common/dto/playableItemDto';
import { getMediaFrameRateFromItem } from '@mimir/common/util/playableItemUtils';
import type { Logger } from '@mimir/common/types/logger';
import { frac } from '@mimir/common/util/integerMath';


export function getFramePtsConversion(
  item: PlayableItemDto,
  artifact: PlayableArtifact,
  browser: BrowserBehavior
): FramePtsConversion {
  const frameDuration = item.frameDuration;
  let delay: FirstFramePtsDelay;
  let usesMuxJs = false;

  if ((artifact.type === 'flatWebProxy') || (artifact.type === 'flatEditProxy')) {
    delay = ffmpegFlatMp4AacChromeDelay;
    if (browser.browserAacLatencyBehavior === 'browser-does-not-skip-init-samples') {
      // Out web/edit proxy files has a init AAC packet with negative timestamp
      // we need to explictly add offset for this packet if the browser does not
      // handle this correctly.
      delay = { ...delay, aacPackets: 1 };
    }
  } else if (artifact.type === 'stream') {

    invariant(artifact.streamSet.length > 0, 'Did not expect empty streamSet');
    switch (artifact.streamSet[0]) {
      case StreamRoleId.LiveRecordWebproxyTs:
        usesMuxJs = true;
        delay = calculateDelayFromTsStream(item.liveRecordingInfo ? item.liveRecordingInfo.webproxyData : null, frameDuration);
        break;

      case StreamRoleId.LiveRecordHighresTs:
        usesMuxJs = true;
        delay = calculateDelayFromTsStream(item.liveRecordingInfo ? item.liveRecordingInfo.highresData : null, frameDuration);
        break;

      case StreamRoleId.LiveRecordAudioOnlyHighresTs:
        usesMuxJs = true;
        delay = calculateDelayFromTsStream(item.liveRecordingInfo ? item.liveRecordingInfo.highresData : null, frameDuration);
        break;

      case StreamRoleId.LiveRecordAudioOnlyProxyTs:
        usesMuxJs = true;
        delay = calculateDelayFromTsStream(item.liveRecordingInfo ? item.liveRecordingInfo.audioProxyData : null, frameDuration);
        break;

      case StreamRoleId.BrowsePreviewVideo:
      case StreamRoleId.BrowsePreviewAudioDownmix:
      case StreamRoleId.BrowsePreviewVideoAudioDownMix:
        delay = keldaLiveCmafChromeDelay;
        break;
      default:
        throw new Error(`Unsupported playable stream role: ${artifact.streamSet[0]}`);
    }
  } else if (artifact.type === 'tams') {
    // TODO: For frame accuracy we probably need to figure out this. I guess
    // the playable item DTO should just contain this information directly.
    // Which could be derived by querying the TAMS server in the backend.
    delay = browserVideoElementDelay;
  } else {
    // Ideally I'd just have the else if here for the last missing artifact type, but then the TS
    // compiler complain that delay is not assigned. I have no idea why. Seems like a bug.
    invariant(artifact.type === 'flatHighRes', `Unknown playable artifact type: ${artifact.type}`);
    // The highres could be anything. Assume no delay.
    delay = { };
  }

  return {
    delay,
    frameDuration,
    minimumFrameIndex: usesMuxJs && browser.hidesFirstFrameOfMuxJsTransmuxedTs ? 1 : 0
  };
}

const browserVideoElementDelay: FirstFramePtsDelay = {
  fuzzBias: 0.0001,
  roundingBias: -0.0000000000001
};

/**
 * Flat-file MP4 AAC encoding using FFmpeg as implemented by mxcoder and Kelda
 * live post-flattening.
 */
const ffmpegFlatMp4AacChromeDelay: FirstFramePtsDelay = {
  ...browserVideoElementDelay,
};

const keldaLiveCmafChromeDelay: FirstFramePtsDelay = {
  ...browserVideoElementDelay,
};

const liveTsWithBFramesTransmuxChromeDelay: FirstFramePtsDelay = {
  ...browserVideoElementDelay,
  frames: 1
};

function calculateDelayFromTsStream(
  tsStream: TsStream | null,
  frameDuration: FuzzyValue,
): FirstFramePtsDelay {
  // For items without dts information for the first frame we just assume
  // Elemental encoder with B-frames enabled.
  const delay = liveTsWithBFramesTransmuxChromeDelay;

  if ((tsStream === null) || (tsStream.firstSegmentFirstFrameDts === undefined)) {
    return delay;
  }

  // We can override the actual number of frames of delay by the actual value
  // by looking at the delta between the first dts and pts. We do this
  // because Chrome sets the zero point at the timestamp of the first DTS and
  // not the PTS.
  const dts = tsStream.firstSegmentFirstFrameDts;
  const pts = tsStream.firstSegmentFirstFramePts;
  const offset = fractionToFuzzy(frac(pts - dts, MPEGTS_TIMEBASE));
  const delayFraction = divideFuzzy(offset, frameDuration);
  if (delayFraction.precise?.denominator === 1) {
    delay.frames = delayFraction.precise.numerator;
  } else {
    delay.frames = Math.round(delayFraction.approximate);
  }
  return delay;
}

export function getStartTimeSeconds(logger: Logger, item: Pick<Item, 'mediaFrameRate' | 'technicalMetadata' | 'timecode'>, startTimeOffsetInSeconds: number = 0): Fraction | null {
  if (!item.timecode) return null;

  const mediaFrameRate = getMediaFrameRateFromItem(item);
  if (mediaFrameRate == null) return null;

  return getTimeInSecondsFromTimecodeString(logger, item.timecode, mediaFrameRate, startTimeOffsetInSeconds);
}
